import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, ViewEncapsulation } from "@angular/core";
import dayjs from "dayjs";
import { DateAdapter } from "@angular/material/core";

@Component({
	selector: 'multi-date-picker',
	templateUrl: './multi-date-picker.component.html',
	styleUrls: ['./multi-date-picker.component.scss'],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiDatePickerComponent implements OnChanges {
	@Input() isError = false;
	@Input() dates: string[] = [];
	@Input() type: 'single' | 'range' | 'multi' = 'single';
	@Input() disabled = false;
	@Input() isSingle = false;
	@Input() disableDates?: string[] = [];
	@Input() hideLabel?: boolean = false;
	@Input() placeholderText?: string = ''; 
	@Input() maxDate?: string | Date = undefined;
	@Input() minDate?: string | Date = undefined;
	@Output() orderDatesEvent: EventEmitter<string[]> = new EventEmitter<string[]>();
	@ViewChild('datePickerContainer', { static: false }) datePickerContainer: ElementRef<HTMLDivElement>;
	isDatePickerOpen = false;
	datepickerYPosition: 'above' | 'below' = 'above';
	dateRange: {
		begin: any;
		end: any;
	};
	daysSelected: {
		date: dayjs.Dayjs;
		unix: number;
	}[] = [];
	selectedDateView: string[] = [];
	event: any;
	public CLOSE_ON_SELECTED = false;

	selectedDateViewString = '';

	private lastEmittedRange: string[] = [];

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly dateAdapter: DateAdapter<Date>
  ){

  }
  ngOnChanges(changes: SimpleChanges){
    this.dateAdapter.setLocale('en-GB');
    if(changes.type){
      this.reset();
    }
    if(changes.dates){
      if(this.type === 'range' && this.dates.length){
          this.dateRange = {
          begin: this.dates[0],
          end: this.dates[1]
        }
        this.lastEmittedRange = [...this.dates];
      }else {
        this.daysSelected = this.dates.map((item)=>{
          return {
            date: dayjs(item),
            unix: dayjs(item).unix()
          }
        })
        this.selectedDateViewString = this.generateDatesView();
      }
    }
  }
	viewChangeHandler(event): void {
        console.log({event});
    }


	dateFilterFn = (selectedDate: any): boolean => {
		if (this.maxDate) {
			const max = dayjs(this.maxDate);
			if (dayjs(selectedDate).isAfter(max, 'date')) {
				return false;
			}
		}

		if (this.minDate) {
			const min = dayjs(this.minDate);
			if (dayjs(selectedDate).isBefore(min, 'date')) {
				return false;
			}
		}

		if (!this.disableDates?.length) {
			return true;
		}
		const shouldDisableDate = this.disableDates.some((date) => dayjs(date).isSame(selectedDate));
		return !shouldDisableDate;
	};

	reset() {
		this.daysSelected = [];
		this.selectedDateView = [];
		this.selectedDateViewString = '';
		this.dateRange = undefined;
		this.lastEmittedRange = [];
		this.orderDatesEvent.emit([]);
	}

	onDateRangeChange(event) {
		this.daysSelected = [];
		if (event?.target?.value) {
			const dateRange = event.target.value;
			const startDate = dayjs(dateRange.begin);
			const endDate = dayjs(dateRange.end);
			if(startDate.isSame(endDate, 'date')){
				this.daysSelected.push({ date: startDate, unix: startDate.unix() });
			}else{
				this.daysSelected.push({ date: startDate, unix: startDate.unix() });
				this.daysSelected.push({ date: endDate, unix: endDate.unix() });
			}
		}
		const newValues = this.daysSelected.map((item) => dayjs(item.date).format());
		const isSame = this.lastEmittedRange.length === newValues.length &&
			this.lastEmittedRange.every((v, i) => v === newValues[i]);
		if (!isSame) {
			this.lastEmittedRange = newValues;
			this.orderDatesEvent.emit(newValues);
		}
	}
	sortDates(
		model: {
			date: dayjs.Dayjs;
			unix: number;
		}[]
	) {
		if (model.length) {
			return model.sort(function (a, b) {
				return a['unix'] - b['unix'];
			});
		}
		return [];
	}
	isSelected = (event: any) => {
		const dayjsDateObject = dayjs(event);
		const isSelected = this.daysSelected.find((x) => dayjs(x.date).isSame(dayjsDateObject, 'date'));
	
		return isSelected ? 'selected' : null;
	};

	select(event: any, calendar: any) {
		if (!this.isSingle) {
			if (event) {
				const date = new Date(dayjs(event).format());
				const index = this._findDate(date);
				if (index === -1) {
					this.daysSelected.push({
						date: dayjs(date),
						unix: dayjs(event).unix(),
					});
				} else {
					this.daysSelected.splice(index, 1);
				}
			}
		} else {
			const date = new Date(dayjs(event).format());
			const index = this._findDate(date);
			if (index === -1) {
				this.daysSelected = [
					{
						date: dayjs(date),
						unix: dayjs(event).unix(),
					},
				];
			} else {
				this.daysSelected.splice(index, 1);
			}
		}
		if (!this.daysSelected.length) {
			this.selectedDateViewString = '';
		}
		calendar.updateTodaysDate();
		this.orderDatesEvent.emit(this.daysSelected.map((item) => dayjs(item.date).format()));
	}

	onClose() {
		this.daysSelected = this.sortDates(this.daysSelected);
		this.selectedDateViewString = this.generateDatesView();
		this.isDatePickerOpen = false;
	}

	generateDatesView() {
		return this.daysSelected.map((item) => item.date.format('DD.MM.YYYY')).join(' | ');
	}

	// generateDatesView2(dates: dayjs.Dayjs[]){
	//   if(!dates.length){
	//     this.reset();
	//     throw new Error('[MultiDatePicker (generateDatesView)] dates is empty!');
	//   }
	//   const selectedDateView: string[] = []
	//   // this.selectedDateView = [];
	//   let dateRangeStart: string = '';
	//   let dateRangeEnd: string = '';
	//   dates.forEach((currentDate: dayjs.Dayjs, index: number)=>{
	//     // const currentDate = dayjs(date);
	//     const formattedCurrentDate = currentDate.format();
	//     if(!dateRangeStart){
	//       dateRangeStart = currentDate.format();
	//       dateRangeEnd = currentDate.format();
	//       return;
	//     }
	//     const isNextDay = dayjs(dayjs(dateRangeEnd).add(1, 'day').format()).isSame(formattedCurrentDate, 'date');
	//     // const isBefore = dayjs(date).isBefore(dayjs(dateRangeStart));
	//     switch(isNextDay){
	//       case true:
	//         dateRangeEnd = currentDate.format();
	//         break;
	//       case false:
	//         if(dateRangeEnd && dateRangeStart!==dateRangeEnd){
	//           selectedDateView.push(`${dayjs(dateRangeStart).format('DD.MM.YYYY')} - ${dayjs(dateRangeEnd).format('DD.MM.YYYY')}`);
	//         }else {
	//           selectedDateView.push(`${dayjs(dateRangeStart).format('DD.MM.YYYY')}`)
	//         }
	//         dateRangeStart = currentDate.format();
	//         dateRangeEnd = currentDate.format();
	//     }
	//     if(index === (dates.length-1)){
	//       if(dateRangeEnd && dateRangeStart!==dateRangeEnd){
	//         selectedDateView.push(`${dayjs(dateRangeStart).format('DD.MM.YYYY')} - ${dayjs(dateRangeEnd).format('DD.MM.YYYY')}`);
	//         return;
	//       }
	//       selectedDateView.push(dayjs(dateRangeStart).format('DD.MM.YYYY'));

	//     }
	//   })
	//   return selectedDateView.join(' | ');
	// }

	private _findDate(date: Date): number {
		return this.daysSelected.findIndex((item) => dayjs(dayjs(date)).isSame(item.date, 'date'));
	}

	onDatePickerTriggerBtnClick(event: MouseEvent, top, bottom): void {
		if (this.type === 'single') {
			const y: number = (this.datePickerContainer.nativeElement.getBoundingClientRect() as any).y;
			const hasEnoughSpaceAtTop: boolean = y > 438;
			if (hasEnoughSpaceAtTop) {
				this.datepickerYPosition = 'above';
				this.cdr.detectChanges();
				top.click();
			} else {
				this.datepickerYPosition = 'below';
				this.cdr.detectChanges();
				bottom.click();
			}
			this.isDatePickerOpen = true;
		}
		event.stopPropagation();
		event.preventDefault();
	}
}
